import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Mail, Check, UserPlus } from "lucide-react";
import { InviteService } from "../../services/inviteService";
import { usePermissions } from "../../hooks/usePermissions";

const SendInviteDialog = ({
  organizationId,
  businessUnitId,
  businessUnit,
  defaultRole = "business_user",
  onInviteSent,
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isOrgAdmin, isBusinessUnitHead } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    email: "",
    role: defaultRole,
    invited_name: "",
    message: "",
  });

  // Get available roles based on user permissions
  const getAvailableRoles = () => {
    const roles = [];

    if (isOrgAdmin) {
      roles.push(
        { value: "org_admin", label: "Organization Admin" },
        { value: "business_unit_head", label: "Business Unit Head" },
        { value: "business_user", label: "Business User" }
      );
    } else if (isBusinessUnitHead) {
      roles.push({ value: "business_user", label: "Business User" });
    }

    return roles;
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      return "Email is required";
    }

    if (!formData.email.includes("@")) {
      return "Please enter a valid email address";
    }

    if (!formData.role) {
      return "Role selection is required";
    }

    // Business unit is required for business unit head and business user roles
    if (
      (formData.role === "business_unit_head" ||
        formData.role === "business_user") &&
      !businessUnitId
    ) {
      return "Business unit is required for this role";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const inviteData = {
        email: formData.email.trim(),
        role: formData.role,
        organization_id: organizationId,
        ...(businessUnitId && { business_unit_id: businessUnitId }),
        ...(formData.invited_name.trim() && {
          invited_name: formData.invited_name.trim(),
        }),
        ...(formData.message.trim() && { message: formData.message.trim() }),
      };

      await InviteService.sendInvite(inviteData, getAccessTokenSilently);

      setSuccess("Invitation sent successfully!");

      // Reset form
      setFormData({
        email: "",
        role: defaultRole,
        invited_name: "",
        message: "",
      });

      if (onInviteSent) {
        onInviteSent();
      }

      // Close dialog after successful invite
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setFormData({
      email: "",
      role: defaultRole,
      invited_name: "",
      message: "",
    });
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const availableRoles = getAvailableRoles();

  // Don't render if user has no permissions to send invites
  if (availableRoles.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetDialog();
      }}
    >
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Send Invite
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Invitation</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Invite a new user to join{" "}
            {businessUnit ? `"${businessUnit.name}"` : "your organization"}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          {success && (
            <Alert className="border-green-200 bg-green-50 text-green-800">
              <Check className="h-4 w-4" />
              {success}
            </Alert>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Role *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange("role", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Name (optional) */}
          <div className="space-y-2">
            <Label htmlFor="invited_name">Full Name (Optional)</Label>
            <Input
              id="invited_name"
              placeholder="John Doe"
              value={formData.invited_name}
              onChange={(e) =>
                handleInputChange("invited_name", e.target.value)
              }
            />
          </div>

          {/* Message (optional) */}
          <div className="space-y-2">
            <Label htmlFor="message">Personal Message (Optional)</Label>
            <Textarea
              id="message"
              placeholder="Welcome to our team! We're excited to have you join us."
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SendInviteDialog;
